import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useCurrentUserId } from "./use-current-user";
import { useProjectId } from "./use-project-id";

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string | null;
  is_read: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
}

export function useNotifications() {
  const queryClient = useQueryClient();
  const { data: userId } = useCurrentUserId();
  const { data: projectId } = useProjectId();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ["notifications", userId, projectId],
    queryFn: async () => {
      if (!userId || !projectId) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("id, user_id, type, title, message, is_read, metadata, created_at")
        .eq("user_id", userId)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!userId && !!projectId,
    staleTime: 30_000,
  });

  // Subscribe to realtime notifications — one channel per userId.
  // Deps intentionally exclude queryClient (stable across renders) so the
  // channel is not torn down/rebuilt on unrelated re-renders. The cleanup
  // waits until the channel has actually reached SUBSCRIBED (or errored)
  // before calling removeChannel, avoiding the "WebSocket is closed before
  // the connection is established" warning on rapid re-mounts.
  useEffect(() => {
    if (!userId) return;

    let status: "pending" | "subscribed" | "closed" = "pending";
    let unmounted = false;
    let loggedError = false;

    const channel = supabase
      .channel(`notifications-realtime:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        }
      )
      .subscribe((s) => {
        if (s === "SUBSCRIBED") {
          status = "subscribed";
          // If the effect was already cleaned up while the handshake was in
          // flight, remove now that the channel is safely open.
          if (unmounted) {
            supabase.removeChannel(channel);
          }
        } else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT") {
          status = "closed";
          if (!loggedError) {
            loggedError = true;
            console.warn(`[notifications] realtime channel ${s}`);
          }
        } else if (s === "CLOSED") {
          status = "closed";
        }
      });

    return () => {
      unmounted = true;
      // Only remove if the handshake has completed (or failed). Otherwise
      // the removal is deferred to the subscribe callback above.
      if (status !== "pending") {
        supabase.removeChannel(channel);
      }
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  return {
    notifications: notifications || [],
    unreadCount,
    isLoading,
  };
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { data: userId } = useCurrentUserId();
  const { data: projectId } = useProjectId();

  return useMutation({
    mutationFn: async () => {
      if (!userId || !projectId) return;

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", userId)
        .eq("project_id", projectId)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useClearNotifications() {
  const queryClient = useQueryClient();
  const { data: userId } = useCurrentUserId();
  const { data: projectId } = useProjectId();

  return useMutation({
    mutationFn: async () => {
      if (!userId || !projectId) return;

      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("user_id", userId)
        .eq("project_id", projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
